import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams
} from 'react-router-dom'
import {
    Col,
    Container,
    Row,
    TopBar,
    CardClientDetails,
    Button,
    Spinner
} from '../../components'
import {
    useBackAction,
    useBindDispatch,
    useGetClient,
    useInteractions
} from 'hooks'
import {
    constants,
    isClientInfoComplete,
    routes,
    getConnectUser,
    getStorage
} from '../../helpers'
import { useTranslation } from 'react-i18next'
import { ClientStatus as ClientStatusEnum } from '../../storeTypes/clientTypes'
import ClientStatus from '../../components/card/ClientStatus'
import { findPermission } from '../../helpers/findPermission'
import { translateGender } from './helpers/villageClientHelpers'
import { getClientTransactionDetails } from 'pages/payment/helpers/getClientTransactionData'
import { VillageClientPayment } from './VillageClientPayment'
import { VillageClientPacket } from './VillageClientPacket'
import { buildPaymentSummaryProps } from 'pages/payment/helpers/buildPaymentSummaryProps'
import { isAnyDelivered } from 'helpers/isAnyDelivered'

export const VillagePrevClientDetails = () => {
    const { t } = useTranslation()
    const { villageId, clientId } = useParams()
    const { prevSelectedClient } = useSelector(
        (state) => state.villagePreviousClientList[villageId] || {}
    )
    const { client, loadingClient } = useGetClient(prevSelectedClient, clientId)
    const [searchParams] = useSearchParams()
    const { state } = useLocation()
    const reload = searchParams.get('reload')
    const fromURL = state?.from || searchParams.get('from')

    const {
        selectVillagePrevClientActionHandler,
        navbarHandler,
        openVillagePageHandler,
        updateSingleClientBalAndSmsHandler,
        clearSingleClientDataHandler,
        transactionsHandler,
        getClientOrdersHandler,
        resetClientOrdersHandler
    } = useBindDispatch()
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const { veVillages, username } = getConnectUser()

    const { selectedVeVillages } = useSelector((state) => state.ve)

    const { clientOrders } = useSelector((state) => state.clientOrders)

    const { transactions } = useSelector(
        (state) => state.transactions[villageId] || {}
    )

    const reEnrollmentHandler = () => {
        const localClientData = JSON.parse(
            localStorage.getItem(constants.CLIENT_DETAIL_LOCAL_STORE)
        )
        localClientData.village_name = villageName
        localClientData.village_id = villageId

        localStorage.setItem(
            constants.CLIENT_FOR_ENROLLMENT_STORE,
            JSON.stringify({
                ...localClientData,
                form_type: constants.FORM_TYPES.RE_ENROLLMENT
            })
        )

        return navigate(`/${routes.enroll}/${villageId}?from=${pathname}`)
    }

    const modifyHandler = (modificationType, nextURL = '') => {
        const localClientData = JSON.parse(
            localStorage.getItem(constants.CLIENT_DETAIL_LOCAL_STORE)
        )
        localClientData.village_name = villageName
        localClientData.village_id = villageId

        localStorage.setItem(
            constants.CLIENT_FOR_ENROLLMENT_STORE,
            JSON.stringify({
                ...localClientData,
                form_type: constants.FORM_TYPES.MODIFICATION,
                modification:
                    modificationType ||
                    constants.CLIENT_MODIFICATION_TYPES.DETAILS
            })
        )
        const next = nextURL ? `&next=${nextURL}` : ''
        return navigate(
            `/${routes.enroll}/${localClientData.village_id}?from=${pathname}${next}`
        )
    }

    const { interactionHandler } = useInteractions({
        client,
        username,
        villageId,
        modifyHandler,
        reEnrollmentHandler
    })

    const { clientBalance = 0, lastPaymentDate } = useMemo(
        () =>
            getClientTransactionDetails(
                client || { client_code: '', balance: 0 },
                transactions
            ),
        [client, transactions]
    )

    const villages = selectedVeVillages || veVillages

    // get village name
    const { name: villageName } = villages?.find(
        ({ sf_id: sfId }) => sfId === villageId
    ) || { name: '' }
    const userPermissions = getStorage(constants.USER_PERMISSIONS)

    const displayVisitButton =
        findPermission(userPermissions, constants.CAN_RECORD_HOME_VISIT) &&
        (client?.duplicate === false || client?.duplicate === undefined)

    // handles on device back button press or back swipe
    useBackAction(fromURL || `/village/${villageId}/`)

    // show menu bar
    useEffect(() => {
        navbarHandler(constants.SHOW_NAVBAR)
        document.title = t('clientDetails.pageTitle')

        return () => {
            clearSingleClientDataHandler()
        }
    }, [])

    useEffect(() => {
        selectVillagePrevClientActionHandler({ clientId, villageId })
    }, [])

    useEffect(() => {
        if (reload) {
            updateSingleClientBalAndSmsHandler({
                villageId,
                clientId
            })
        }
    }, [reload])

    useEffect(() => {
        transactionsHandler({ villageId })
    }, [])

    useEffect(() => {
        getClientOrdersHandler(clientId)
    }, [])

    useEffect(() => {
        return () => {
            resetClientOrdersHandler()
        }
    }, [])

    const canModifyClient = !client?.duplicate

    const openVisitPageHandler = () => {
        if (isClientInfoComplete(client)) {
            localStorage.setItem(
                constants.CLIENT_FOR_VISIT_STORE,
                JSON.stringify({
                    clientCode: client.client_code,
                    villageId: client.village_id || villageId,
                    clientName: client.firstname,
                    origin: constants.ORIGIN_PREV_CLIENT_DETAIL
                })
            )
            navigate('/visit')
        } else {
            modifyHandler(constants.CLIENT_MODIFICATION_TYPES.DETAILS, '/visit')
        }
    }

    const backNavigationHandler = () => {
        openVillagePageHandler({
            filterOptions: [],
            villageId,
            clientTab: constants.VILLAGE_PAGE_TABS.PREVIOUS_CLIENT_TAB
        })
        return navigate(fromURL || `/village/${villageId}/`)
    }

    const villageClientDetailsRender = (clientDetailsData) => {
        const {
            fullname,
            client_code: clientCode,
            sex: Genre,
            group_leader: groupLeader,
            head_of_household: headOfHouseHold,
            zone,
            phone,
            age,
            duplicate,
            recent_year_delivered: recentYearDelivered,
            recent_year_enrolled: recentYearEnrolled,
            all_enrollment: allEnrollment
        } = clientDetailsData
        return (
            <>
                <div className="sticky-header" id="sticky-header">
                    <Container>
                        <TopBar back backNavigation={backNavigationHandler}>
                            {villageName}
                        </TopBar>
                    </Container>
                </div>
                <div
                    className="village-client-details-parent page-wrapper"
                    data-testid="client-details-page"
                >
                    <Container>
                        <Row>
                            <Col md={12}>
                                <ClientStatus
                                    status={
                                        duplicate
                                            ? ClientStatusEnum.DUPLICATE
                                            : ClientStatusEnum.POTENTIAL
                                    }
                                />
                            </Col>
                        </Row>
                        <Row className="content-center">
                            <Col md={4}>
                                <CardClientDetails
                                    className="village-prev--client-card-details"
                                    clientCode={clientCode}
                                    clientPhone={phone}
                                    otherClientData={{
                                        [t('gender')]: translateGender(
                                            Genre,
                                            t
                                        ),
                                        [t('age')]: age,
                                        [t('groupRole')]: groupLeader,
                                        [t('familyLeader')]: headOfHouseHold,
                                        [t('zone')]: zone,
                                        [t('village.lastYearDelivered')]:
                                            recentYearDelivered || '-',
                                        [t('village.registrationDate')]:
                                            recentYearEnrolled || '-'
                                    }}
                                    clientPage={
                                        constants.CLIENT_TYPES.PREVIOUS_CLIENT
                                    }
                                    isClientDuplicate={client?.duplicate}
                                    clientStatus={client?.client_status}
                                    interactionHandler={interactionHandler}
                                >
                                    {fullname}
                                </CardClientDetails>
                            </Col>
                        </Row>
                        <Row
                            className="village-client-details-parent"
                            data-testid="client-details-payment"
                        >
                            <Col md={12}>
                                <VillageClientPayment
                                    paymentSummary={buildPaymentSummaryProps(
                                        {
                                            allEnrollment,
                                            balance: clientBalance
                                        },
                                        lastPaymentDate
                                            ? t('dateMonthYear', {
                                                  date: new Date(
                                                      lastPaymentDate
                                                  )
                                              })
                                            : '-'
                                    )}
                                    isAnyDelivered={isAnyDelivered(
                                        clientOrders
                                    )}
                                    fromURL={fromURL}
                                />
                            </Col>
                        </Row>
                        <Row
                            className="village-client-details-parent"
                            data-testid="client-details-packages"
                        >
                            <Col md={12}>
                                <VillageClientPacket
                                    goalItems={clientOrders}
                                    allowModification={canModifyClient}
                                    modifyHandler={() =>
                                        modifyHandler(
                                            constants
                                                .CLIENT_MODIFICATION_TYPES
                                                .PACKAGES
                                        )
                                    }
                                />
                            </Col>
                        </Row>
                        {displayVisitButton && (
                            <div className="visit-new-container">
                                <Button
                                    className="visit-new-btn"
                                    style="primary"
                                    onClick={openVisitPageHandler}
                                    data-testid="button-new-visit"
                                    id="btn-new-visit"
                                >
                                    {t('enrollment.newVisit')}
                                </Button>
                            </div>
                        )}
                    </Container>
                </div>
            </>
        )
    }

    const singleClientLoading = (
        <Container>
            <Row>
                <Col md={12}>
                    <div className="loading-wrapper">
                        <p className="loading-text">
                            {t('fetchingPaymentLoader')}
                        </p>
                        <Spinner size="120" />
                    </div>
                </Col>
            </Row>
        </Container>
    )

    const villageClientDetailPage = loadingClient
        ? singleClientLoading
        : client && villageClientDetailsRender(client)

    return villageClientDetailPage
}
